import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatCurrency, formatNumber } from '../../../utils/formatters';


// ── Tipi locali ──────────────────────────────────────────────────────────
interface Banca { id: number; nome: string; attiva: boolean; }

interface ProssimaRata {
  dataScadenza: string;
  importoRata: number;
  numeroRata: number;
}

interface Finanziamento {
  id: number;
  societaId: number;
  societaNome?: string;
  bancaId?: number;
  nomeBanca: string;
  descrizione: string;
  importoOriginale: number;
  dataErogazione: string;
  durataAnni: number;
  tipoAmmortamento: string;
  tipoTasso: string;
  tassoIniziale: number;
  cadenzaRata: string;
  dataPrimaRata: string;
  haPreammortamento: boolean;
  durataPreammortamentoMesi?: number;
  tipoAsset?: string;
  descrizioneAsset?: string;
  valoreStimatoAsset?: number;
  stato: string;
  note?: string;
  rateTotali: number;
  ratePagate: number;
  capitalePagato: number;
  interessiPagati: number;
  debitoResiduo: number;
  prossimaNScadenza?: ProssimaRata;
  storiciTasso?: StoricoTasso[];
}

interface Rata {
  id: number;
  finanziamentoId: number;
  numeroRata: number;
  dataScadenza: string;
  importoRata: number;
  quotaCapitale: number;
  quotaInteressi: number;
  debitoResiduo: number;
  isPagata: boolean;
  isPreammortamento: boolean;
}

interface StoricoTasso {
  id: number;
  tassoNuovo: number;
  tassoVecchio: number;
  dataDecorrenza: string;
  motivazione?: string;
  utenteNome?: string;
}

interface DashboardData {
  totaleFinanziato: number;
  debitoResidualeTotale: number;
  capitalePagatoTotale: number;
  interessiPagatiTotale: number;
  prossimaRata?: { data: string; importoTotale: number; numeroFinanziamenti: number };
  impegniAnni: { anno: number; totaleRate: number; totaleCapitale: number; totaleInteressi: number }[];
  fisso: { count: number; importoOriginale: number };
  variabile: { count: number; importoOriginale: number };
  ammortamento: {
    francese:  { count: number };
    italiano:  { count: number };
    americano: { count: number };
    bullet:    { count: number };
  };
  inScadenza: { id: number; descrizione: string; nomeBanca: string; dataUltimaRata: string; debitoResiduo: number }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtData = (d: string) =>
  new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

const giorniA = (d: string) => {
  const diff = new Date(d).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const TIPO_AMM_LABELS: Record<string, string> = {
  FRANCESE:  'Francese',
  ITALIANO:  'Italiano',
  AMERICANO: 'Americano',
  BULLET:    'Bullet',
};

// ── Calcolo ammortamento client-side per anteprima ────────────────────────
function calcolaAnteprimaPiano(form: Record<string, any>): AnteprimaRata[] {
  const capitale = Number(form.importoOriginale) || 0;
  const tasso = Number(form.tassoIniziale) || 0;
  const durataAnni = Number(form.durataAnni) || 1;
  const cadenza = form.cadenzaRata || 'MENSILE';
  const tipo = form.tipoAmmortamento || 'FRANCESE';
  const periodi: Record<string, number> = { MENSILE: 12, TRIMESTRALE: 4, SEMESTRALE: 2, ANNUALE: 1 };
  const n = durataAnni * periodi[cadenza];
  const i = (tasso / 100) / periodi[cadenza];
  const r2 = (x: number) => Math.round(x * 100) / 100;

  if (!capitale || !n) return [];

  const rows: AnteprimaRata[] = [];
  let residuo = capitale;

  if (tipo === 'FRANCESE') {
    const rata = i > 0 ? r2(capitale * i / (1 - Math.pow(1 + i, -n))) : r2(capitale / n);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qi = r2(residuo * i);
      const qc = k === n - 1 ? r2(residuo) : r2(rata - qi);
      residuo = r2(residuo - qc);
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: Math.max(residuo, 0) });
    }
  } else if (tipo === 'ITALIANO') {
    const qcC = r2(capitale / n);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qi = r2(residuo * i);
      const qc = k === n - 1 ? r2(residuo) : qcC;
      residuo = r2(residuo - qc);
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: Math.max(residuo, 0) });
    }
  } else if (tipo === 'AMERICANO') {
    const qi = r2(capitale * i);
    for (let k = 0; k < Math.min(n, 12); k++) {
      const qc = k === n - 1 ? capitale : 0;
      rows.push({ n: k + 1, qi, qc, rata: r2(qc + qi), residuo: k === n - 1 ? 0 : capitale });
    }
  } else {
    rows.push({ n: 1, qi: r2(capitale * (tasso / 100) * durataAnni), qc: capitale, rata: r2(capitale + capitale * (tasso / 100) * durataAnni), residuo: 0 });
  }
  return rows;
}

interface AnteprimaRata { n: number; qi: number; qc: number; rata: number; residuo: number; }

interface ProiezioneFin {
  finanziamentoId: number;
  descrizione: string;
  banca?: { id: number; nome: string } | null;
  interessiPagatiReali: number;
  interessiPagatiTeorici: number;
  deltaPct: number;
  rateAnalizzate: number;
  proiezioneAttendibile: boolean;
  interessiResiduiTeorici: number;
  interessiResiduiStimati: number;
}

interface ProiezioneDashboard {
  globale: {
    interessiPagatiReali: number;
    interessiPagatiTeorici: number;
    deltaPct: number;
    rateAnalizzate: number;
    interessiResiduiTeorici: number;
    interessiResiduiStimati: number;
    finanziamentiAnalizzati: number;
    finanziamentiTotali: number;
    sogliaMinRate: number;
  };
  finanziamenti: ProiezioneFin[];
  perAnno: { anno: number; interessiReali: number }[];
}

// ── Componente principale ─────────────────────────────────────────────────
export default function PianoFinanziamenti() {
  const navigate = useNavigate();
  const [finanziamenti,  setFinanziamenti]  = useState<Finanziamento[]>([]);
  const [dashboard,      setDashboard]      = useState<DashboardData | null>(null);
  const [proiezione,     setProiezione]     = useState<ProiezioneDashboard | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Filtri
  const [filterStato, setFilterStato] = useState('ATTIVO');

  // Modal tasso
  const [modalTasso, setModalTasso] = useState<Finanziamento | null>(null);

  // Toggle anni passati nel grafico impegni
  const [mostraAnniPassati, setMostraAnniPassati] = useState(false);

  // Form tasso
  const [formTasso, setFormTasso] = useState<Record<string, any>>({
    nuovoTasso: '', dataDecorrenza: new Date().toISOString().split('T')[0], motivazione: '',
  });
  const [tassoSaving, setTassoSaving] = useState(false);
  const [tassoMsg,    setTassoMsg]    = useState('');

  // ── Caricamento dati ───────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.append('societaId', filterSocietaId);
      if (filterStato && filterStato !== 'TUTTI') params.append('stato', filterStato);

      const proiezQs = new URLSearchParams(filterSocietaId ? { societaId: filterSocietaId } : {});
      const [fRes, dRes, pRes] = await Promise.all([
        // @api GET /api/finanziamenti?societaId=&stato= -> { finanziamenti: [...] }
        apiFetch<{ finanziamenti: Finanziamento[] }>(`/api/finanziamenti?${params}`),
        // @api GET /api/finanziamenti/dashboard?societaId= -> { ...kpi }
        apiFetch<DashboardData>(`/api/finanziamenti/dashboard?${new URLSearchParams(filterSocietaId ? { societaId: filterSocietaId } : {})}`),
        // @api GET /api/movimenti-finanziamento/proiezione-dashboard?societaId= -> { success: true, data: { globale, finanziamenti, perAnno } }
        apiFetch<{ success: boolean; data: ProiezioneDashboard }>(`/api/movimenti-finanziamento/proiezione-dashboard?${proiezQs}`)
          .catch(() => ({ success: false, data: null as unknown as ProiezioneDashboard })),
      ]);
      setFinanziamenti(fRes.finanziamenti);
      setDashboard(dRes);
      setProiezione(pRes?.data ?? null);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [pronta, filterSocietaId, filterStato]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Aggiorna tasso ─────────────────────────────────────────────────────
  const openTasso = async (f: Finanziamento) => {
    setModalTasso(f);
    setTassoMsg('');
    setFormTasso({
      nuovoTasso:     '',
      dataDecorrenza: new Date().toISOString().split('T')[0],
      motivazione:    '',
    });
    // Carica storici
    try {
      const d = await apiFetch<{ finanziamento: Finanziamento }>(`/api/finanziamenti/${f.id}`);
      setModalTasso(d.finanziamento);
    } catch {}
  };

  const aggiornaTasso = async () => {
    if (!modalTasso) return;
    setTassoSaving(true); setTassoMsg('');
    try {
      await apiFetch(`/api/finanziamenti/${modalTasso.id}/tasso`, {
        method: 'PUT',
        body: JSON.stringify(formTasso),
      });
      setTassoMsg('Tasso aggiornato e piano ricalcolato.');
      loadData();
    } catch (e: any) { setTassoMsg(`Errore: ${(e as Error).message}`); }
    finally { setTassoSaving(false); }
  };

  // ── Badge colore stato ────────────────────────────────────────────────
  const badgeStato = (stato: string) => {
    const map: Record<string, string> = {
      ATTIVO: 'bop-badge--success', ESTINTO: 'bop-badge--neutral', SOSPESO: 'bop-badge--warning',
    };
    return `bop-badge ${map[stato] || 'bop-badge--neutral'}`;
  };

  const badgeAmmortamento = (tipo: string) => {
    const map: Record<string, string> = {
      FRANCESE: 'bop-badge--info', ITALIANO: 'bop-badge--success', AMERICANO: 'bop-badge--primary', BULLET: 'bop-badge--neutral',
    };
    return `bop-badge ${map[tipo] || 'bop-badge--neutral'}`;
  };

  // ── Barra progresso residuo ───────────────────────────────────────────
  const BarraResiduo = ({ perc }: { perc: number }) => {
    const modClass = perc < 30 ? 'bop-finanz__progress-bar--success' : perc < 70 ? 'bop-finanz__progress-bar--warning' : 'bop-finanz__progress-bar--danger';
    return (
      <div className="bop-finanz__progress-track">
        <div className={`bop-finanz__progress-bar ${modClass}`} style={{ width: `${Math.min(perc, 100)}%` }} />
      </div>
    );
  };

  // ── Calcolo approssimativo nuova rata per preview tasso ───────────────
  const nuovaRataPreview = useMemo(() => {
    if (!modalTasso || !formTasso.nuovoTasso) return null;
    const tmp = calcolaAnteprimaPiano({
      importoOriginale: modalTasso.debitoResiduo,
      tassoIniziale:    Number(formTasso.nuovoTasso),
      durataAnni:       modalTasso.durataAnni,
      cadenzaRata:      modalTasso.cadenzaRata,
      tipoAmmortamento: modalTasso.tipoAmmortamento,
    });
    return tmp[0]?.rata ?? null;
  }, [modalTasso, formTasso.nuovoTasso]);

  // ── UI ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="bop-page__header">
        <div className="bop-page__header-text">
          <h2 className="bop-page__title">Piano Finanziamenti</h2>
          <p className="bop-page__subtitle">Gestione e monitoraggio dei finanziamenti aziendali</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={() => navigate('/amministrazione/finanziamenti/nuovo')}>
          + Nuovo Finanziamento
        </button>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {/* ── MINI DASHBOARD ──────────────────────────────────────────── */}
      {dashboard && (
        <>
          <div className="bop-finanz__kpi-grid">
            {/* KPI 1 */}
            <div className="bop-card bop-finanz__kpi-card">
              <div className="bop-finanz__kpi-label">
                Totale Finanziato
              </div>
              <div className="bop-finanz__kpi-value">
                {formatCurrency(dashboard.totaleFinanziato)}
              </div>
            </div>

            {/* KPI 2 */}
            <div className="bop-card bop-finanz__kpi-card">
              <div className="bop-finanz__kpi-label">
                Debito Residuale
              </div>
              <div className="bop-finanz__kpi-value bop-finanz__kpi-value--danger">
                {formatCurrency(dashboard.debitoResidualeTotale)}
              </div>
              {dashboard.totaleFinanziato > 0 && (
                <BarraResiduo perc={Math.round(dashboard.debitoResidualeTotale / dashboard.totaleFinanziato * 100)} />
              )}
            </div>

            {/* KPI 3 */}
            <div className="bop-card bop-finanz__kpi-card">
              <div className="bop-finanz__kpi-label">
                Già Rimborsato
              </div>
              <div className="bop-finanz__kpi-value bop-finanz__kpi-value--success">
                {formatCurrency(dashboard.capitalePagatoTotale + dashboard.interessiPagatiTotale)}
              </div>
              <div className="bop-finanz__kpi-sub">
                cap. {formatCurrency(dashboard.capitalePagatoTotale)} + int. {formatCurrency(dashboard.interessiPagatiTotale)}
              </div>
            </div>

            {/* KPI 4 */}
            <div className="bop-card bop-finanz__kpi-card">
              <div className="bop-finanz__kpi-label">
                Prossima Rata
              </div>
              {dashboard.prossimaRata ? (() => {
                const giorni = giorniA(dashboard.prossimaRata.data);
                return (
                  <>
                    <div className="bop-finanz__kpi-value--sm">
                      {formatCurrency(dashboard.prossimaRata.importoTotale)}
                    </div>
                    <div className="bop-finanz__kpi-date">{fmtData(dashboard.prossimaRata.data)}</div>
                    <span className={`bop-finanz__countdown ${giorni < 30 ? 'bop-finanz__countdown--urgent' : ''}`}>
                      tra {giorni} giorni
                    </span>
                  </>
                );
              })() : (
                <div className="bop-finanz__kpi-empty">Nessuna rata in scadenza</div>
              )}
            </div>
          </div>

          {/* ── GRAFICI ──────────────────────────────────────────────── */}
          <div className="bop-finanz__charts-grid">
            {/* ── Card grafico + tabella impegni ── */}
            <div className="bop-card bop-finanz__kpi-card">
              <div className="bop-finanz__impegni-header">
                <div className="bop-finanz__section-title">Impegni per anno</div>
                <div className="bop-finanz__impegni-toggle">
                  <button
                    className={`bop-finanz-det__btn-filter${!mostraAnniPassati ? ' bop-finanz-det__btn-filter--active' : ''}`}
                    onClick={() => setMostraAnniPassati(false)}
                  >
                    Anni futuri
                  </button>
                  <button
                    className={`bop-finanz-det__btn-filter${mostraAnniPassati ? ' bop-finanz-det__btn-filter--active' : ''}`}
                    onClick={() => setMostraAnniPassati(true)}
                  >
                    Tutti gli anni
                  </button>
                </div>
              </div>
              {(() => {
                const annoCorrente = new Date().getFullYear();
                const realiByAnno = new Map<number, number>();
                if (proiezione) {
                  for (const r of proiezione.perAnno) realiByAnno.set(r.anno, r.interessiReali);
                }
                const datiBase = mostraAnniPassati
                  ? dashboard.impegniAnni
                  : dashboard.impegniAnni.filter(d => d.anno >= annoCorrente);
                const datiImpegni = datiBase.map(d => ({
                  ...d,
                  interessiReali: realiByAnno.get(d.anno) ?? 0,
                }));
                const haDatiReali = datiImpegni.some(d => d.interessiReali > 0);
                return datiImpegni.length > 0 ? (
                  <div className="bop-finanz__impegni-grid">
                    {/* Grafico */}
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={datiImpegni} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="anno" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => formatCurrency(Number(v))} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="totaleCapitale" name="Capitale (piano)" fill="#2563eb" stackId="a" />
                        <Bar dataKey="totaleInteressi" name="Interessi (piano)" fill="#f97316" stackId="a" />
                        {haDatiReali && (
                          <Bar dataKey="interessiReali" name="Interessi reali pagati" fill="#dc2626" />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                    {/* Tabella */}
                    <div className="bop-finanz__impegni-table-wrap">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th>Anno</th>
                            <th className="bop-text-right">Capitale</th>
                            <th className="bop-text-right">Int. piano</th>
                            {haDatiReali && <th className="bop-text-right">Int. reali</th>}
                            <th className="bop-text-right">Totale</th>
                          </tr>
                        </thead>
                        <tbody>
                          {datiImpegni.map(d => (
                            <tr
                              key={d.anno}
                              className={
                                d.anno < annoCorrente
                                  ? 'bop-finanz__impegni-row--passato'
                                  : d.anno === annoCorrente
                                  ? 'bop-finanz__impegni-row--corrente'
                                  : ''
                              }
                            >
                              <td>{d.anno}{d.anno === annoCorrente ? ' ●' : ''}</td>
                              <td className="bop-text-right">{formatCurrency(d.totaleCapitale)}</td>
                              <td className="bop-text-right">{formatCurrency(d.totaleInteressi)}</td>
                              {haDatiReali && (
                                <td className="bop-text-right">
                                  {d.interessiReali > 0 ? (
                                    <span className={d.interessiReali > d.totaleInteressi ? 'bop-finanz__reale--alert' : 'bop-finanz__reale--ok'}>
                                      {formatCurrency(d.interessiReali)}
                                    </span>
                                  ) : '—'}
                                </td>
                              )}
                              <td className="bop-text-right"><strong>{formatCurrency(d.totaleRate)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bop-empty-state">Nessun dato disponibile</div>
                );
              })()}
            </div>

            {/* Distribuzione fisso/variabile + in scadenza */}
            <div className="bop-finanz__sidebar-col">
              <div className="bop-card bop-finanz__kpi-card">
                <div className="bop-finanz__section-title--sm">
                  Distribuzione per tasso
                </div>
                <div className="bop-finanz__distrib-flex">
                  <div className="bop-finanz__distrib-group">
                    <span className="bop-badge bop-badge--info">FISSO</span>
                    <div className="bop-finanz__distrib-item">
                      {dashboard.fisso.count} finanz. · {formatCurrency(dashboard.fisso.importoOriginale)}
                    </div>
                  </div>
                  <div className="bop-finanz__distrib-group">
                    <span className="bop-badge bop-badge--warning">VAR</span>
                    <div className="bop-finanz__distrib-item">
                      {dashboard.variabile.count} finanz. · {formatCurrency(dashboard.variabile.importoOriginale)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribuzione per ammortamento */}
              <div className="bop-card bop-finanz__kpi-card">
                <div className="bop-finanz__section-title--sm">
                  Distribuzione per ammortamento
                </div>
                <div className="bop-finanz__distrib-flex">
                  {([
                    { key: 'francese',  label: 'FRA', cls: 'bop-badge--info' },
                    { key: 'italiano',  label: 'ITA', cls: 'bop-badge--success' },
                    { key: 'americano', label: 'USA', cls: 'bop-badge--primary' },
                    { key: 'bullet',    label: 'BUL', cls: 'bop-badge--neutral' },
                  ] as const).map(({ key, label, cls }) => {
                    const count = dashboard.ammortamento[key].count;
                    if (count === 0) return null;
                    return (
                      <div key={key} className="bop-finanz__distrib-group">
                        <span className={`bop-badge ${cls}`}>{label}</span>
                        <div className="bop-finanz__distrib-item">{count} finanz.</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {dashboard.inScadenza.length > 0 && (
                <div className="bop-card bop-finanz__scadenza-card">
                  <div className="bop-finanz__section-title--sm">
                    In scadenza entro 12 mesi
                  </div>
                  {dashboard.inScadenza.map(f => {
                    const mesi = Math.ceil(giorniA(f.dataUltimaRata) / 30);
                    return (
                      <div key={f.id} className="bop-finanz__scadenza-item">
                        <div className="bop-finanz__scadenza-left">
                          <div className="bop-finanz__scadenza-name">{f.descrizione}</div>
                          <div className="bop-finanz__scadenza-detail">{f.nomeBanca} · {fmtData(f.dataUltimaRata)}</div>
                        </div>
                        <div className="bop-finanz__scadenza-right">
                          <div className="bop-finanz__scadenza-name">{formatCurrency(f.debitoResiduo)}</div>
                          <span className="bop-finanz__scadenza-badge">
                            {mesi} mesi
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── TREND REALE vs PIANO TEORICO ─────────────────────────── */}
          {proiezione && (
            <div className="bop-card bop-finanz__trend-card">
              <div className="bop-finanz__trend-header">
                <div className="bop-finanz__section-title">Trend interessi reali vs piano teorico</div>
                <div className="bop-finanz__trend-sub">
                  {proiezione.globale.rateAnalizzate > 0
                    ? `Basato su ${proiezione.globale.rateAnalizzate} rate analizzate (${proiezione.globale.finanziamentiAnalizzati}/${proiezione.globale.finanziamentiTotali} finanziamenti con movimenti caricati)`
                    : `Nessuna rata reale ancora collegata: carica i movimenti dei finanziamenti per vedere il confronto.`}
                </div>
              </div>

              {proiezione.globale.rateAnalizzate === 0 && (
                <div className="bop-empty-state">
                  Per popolare questa sezione vai in <strong>Movimenti Finanziamenti</strong>,
                  registra i pagamenti rate dall'estratto conto e associali a una rata teorica
                  del piano. Servono almeno {proiezione.globale.sogliaMinRate} rate per avere
                  una proiezione attendibile.
                </div>
              )}

              {proiezione.globale.rateAnalizzate > 0 && (
                <>
              {/* Mini KPI */}
              <div className="bop-finanz__trend-kpi-grid">
                <div className="bop-finanz__trend-kpi">
                  <div className="bop-finanz__trend-kpi-label">Interessi reali pagati</div>
                  <div className="bop-finanz__trend-kpi-value">{formatCurrency(proiezione.globale.interessiPagatiReali)}</div>
                </div>
                <div className="bop-finanz__trend-kpi">
                  <div className="bop-finanz__trend-kpi-label">Interessi attesi dal piano</div>
                  <div className="bop-finanz__trend-kpi-value">{formatCurrency(proiezione.globale.interessiPagatiTeorici)}</div>
                  <div className="bop-finanz__trend-kpi-sub">sulle stesse rate</div>
                </div>
                <div className="bop-finanz__trend-kpi">
                  <div className="bop-finanz__trend-kpi-label">Delta medio</div>
                  <div className={`bop-finanz__trend-kpi-value ${proiezione.globale.deltaPct > 0 ? 'bop-finanz__trend-kpi-value--alert' : proiezione.globale.deltaPct < 0 ? 'bop-finanz__trend-kpi-value--ok' : ''}`}>
                    {(proiezione.globale.deltaPct * 100).toFixed(1)}%
                  </div>
                  <div className="bop-finanz__trend-kpi-sub">
                    {proiezione.globale.deltaPct > 0 ? 'Pagati più del piano' : proiezione.globale.deltaPct < 0 ? 'Pagati meno del piano' : 'In linea'}
                  </div>
                </div>
                <div className="bop-finanz__trend-kpi">
                  <div className="bop-finanz__trend-kpi-label">Interessi residui stimati</div>
                  <div className="bop-finanz__trend-kpi-value">{formatCurrency(proiezione.globale.interessiResiduiStimati)}</div>
                  <div className="bop-finanz__trend-kpi-sub">
                    piano: {formatCurrency(proiezione.globale.interessiResiduiTeorici)}
                  </div>
                </div>
              </div>

              {/* Tabella per finanziamento */}
              <div className="bop-finanz__trend-table-wrap">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Finanziamento</th>
                      <th>Banca</th>
                      <th className="bop-text-right">Rate analizzate</th>
                      <th className="bop-text-right">Interessi reali</th>
                      <th className="bop-text-right">Interessi piano</th>
                      <th className="bop-text-right">Δ %</th>
                      <th className="bop-text-right">Residui piano</th>
                      <th className="bop-text-right">Residui stimati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proiezione.finanziamenti.map(f => (
                      <tr
                        key={f.finanziamentoId}
                        className="bop-finanz__trend-row"
                        onClick={() => navigate(`/amministrazione/finanziamenti/${f.finanziamentoId}`)}
                      >
                        <td>{f.descrizione}</td>
                        <td>{f.banca?.nome || '—'}</td>
                        <td className="bop-text-right">
                          {f.rateAnalizzate}
                          {f.rateAnalizzate > 0 && !f.proiezioneAttendibile && (
                            <span className="bop-finanz__trend-warn" title={`Sotto soglia di affidabilità (${proiezione.globale.sogliaMinRate} rate)`}> ⚠</span>
                          )}
                        </td>
                        <td className="bop-text-right">{f.rateAnalizzate > 0 ? formatCurrency(f.interessiPagatiReali) : '—'}</td>
                        <td className="bop-text-right">{f.rateAnalizzate > 0 ? formatCurrency(f.interessiPagatiTeorici) : '—'}</td>
                        <td className={`bop-text-right ${f.deltaPct > 0 ? 'bop-finanz__reale--alert' : f.deltaPct < 0 ? 'bop-finanz__reale--ok' : ''}`}>
                          {f.rateAnalizzate > 0 ? `${(f.deltaPct * 100).toFixed(1)}%` : '—'}
                        </td>
                        <td className="bop-text-right">{formatCurrency(f.interessiResiduiTeorici)}</td>
                        <td className={`bop-text-right ${f.proiezioneAttendibile ? '' : 'bop-text-muted'}`}>
                          {f.proiezioneAttendibile ? formatCurrency(f.interessiResiduiStimati) : `${formatCurrency(f.interessiResiduiTeorici)}*`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bop-finanz__trend-note">
                I residui stimati applicano il delta% empirico ai residui teorici.
                I finanziamenti con meno di {proiezione.globale.sogliaMinRate} rate analizzate (*) mostrano il valore teorico — campione troppo piccolo per una proiezione attendibile.
              </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ── FILTRI ───────────────────────────────────────────────────── */}
      <div className="bop-card bop-finanz__filter-bar">
        {!isUnica && (
          <div className="bop-finanz__filter-group">
            <label className="bop-finanz__filter-label">Società:</label>
            <select className="bop-select bop-finanz__select-wide" value={filterSocietaId}
              onChange={e => setFilterSocietaId(e.target.value)}>
              <option value="">Tutte</option>
              {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        )}
        <div className="bop-finanz__filter-group">
          <label className="bop-finanz__filter-label">Stato:</label>
          <select className="bop-select" value={filterStato} onChange={e => setFilterStato(e.target.value)}>
            <option value="TUTTI">Tutti</option>
            <option value="ATTIVO">Attivi</option>
            <option value="ESTINTO">Estinti</option>
            <option value="SOSPESO">Sospesi</option>
          </select>
        </div>
      </div>

      {/* ── TABELLA ──────────────────────────────────────────────────── */}
      <div className="bop-card">
        {loading ? (
          <div className="bop-empty-state">Caricamento...</div>
        ) : finanziamenti.length === 0 ? (
          <div className="bop-empty-state">Nessun finanziamento trovato.</div>
        ) : (
          <div className="bop-table-wrapper">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Descrizione</th>
                  <th>Tipo</th>
                  <th className="bop-text-right">Importo Orig.</th>
                  <th className="bop-text-right">Debito Residuo</th>
                  <th>Tasso</th>
                  <th>Cadenza</th>
                  <th>Prossima Rata</th>
                  <th className="bop-text-center">Rate</th>
                  <th>Stato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {finanziamenti.map(f => {
                  const percResiduo = f.importoOriginale > 0
                    ? Math.round(f.debitoResiduo / f.importoOriginale * 100) : 0;
                  return (
                    <tr key={f.id} onClick={() => navigate(`/amministrazione/finanziamenti/${f.id}`)}>
                      <td>
                        <div className="bop-finanz__cell-primary">{f.descrizione}</div>
                        <div className="bop-finanz__cell-secondary">{f.nomeBanca}</div>
                      </td>
                      <td>
                        <span className={badgeAmmortamento(f.tipoAmmortamento)}>
                          {TIPO_AMM_LABELS[f.tipoAmmortamento]}
                        </span>
                      </td>
                      <td className="bop-finanz__cell-amount">
                        {formatCurrency(Number(f.importoOriginale))}
                      </td>
                      <td className="bop-text-right">
                        <div className={percResiduo > 70 ? 'bop-finanz__residuo--high' : percResiduo > 30 ? 'bop-finanz__residuo--mid' : 'bop-finanz__residuo--low'}>
                          {formatCurrency(f.debitoResiduo)}
                        </div>
                        <BarraResiduo perc={percResiduo} />
                      </td>
                      <td>
                        <div className="bop-finanz__cell-primary">{formatNumber(f.tassoIniziale)}%</div>
                        <span className={`bop-badge bop-finanz__badge-sm ${f.tipoTasso === 'FISSO' ? 'bop-badge--info' : 'bop-badge--warning'}`}>
                          {f.tipoTasso === 'FISSO' ? 'FIS' : 'VAR'}
                        </span>
                      </td>
                      <td className="bop-finanz__cell-sm">{f.cadenzaRata}</td>
                      <td>
                        {f.prossimaNScadenza ? (
                          <>
                            <div className="bop-finanz__cell-sm">{fmtData(f.prossimaNScadenza.dataScadenza)}</div>
                            <div className="bop-finanz__cell-secondary">{formatCurrency(Number(f.prossimaNScadenza.importoRata))}</div>
                          </>
                        ) : (
                          <span className="bop-text-muted bop-finanz__cell-sm">—</span>
                        )}
                      </td>
                      <td className="bop-text-center bop-finanz__cell-sm">
                        {f.ratePagate}/{f.rateTotali}
                      </td>
                      <td>
                        <span className={badgeStato(f.stato)}>{f.stato}</span>
                      </td>
                      <td>
                        <div className="bop-finanz__actions">
                          {f.tipoTasso === 'VARIABILE' && (
                            <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={(e) => { e.stopPropagation(); openTasso(f); }}>Tasso</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* MODAL: AGGIORNA TASSO                                          */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {modalTasso && (
        <Modal
          title={`Aggiorna Tasso — ${modalTasso.descrizione}`}
          onClose={() => setModalTasso(null)}
          footer={
            <div className="bop-finanz__modal-footer">
              <button className="bop-btn bop-btn-ghost" onClick={() => setModalTasso(null)}>Chiudi</button>
              <button className="bop-btn bop-btn-primary" onClick={aggiornaTasso} disabled={tassoSaving || !formTasso.nuovoTasso}>
                {tassoSaving ? 'Aggiornamento…' : 'Aggiorna Tasso e Ricalcola Piano'}
              </button>
            </div>
          }
        >
          <div className="bop-finanz__vstack">
            <div className="bop-finanz__tasso-current">
              Tasso attuale: <strong>
                {modalTasso.storiciTasso && modalTasso.storiciTasso.length > 0
                  ? formatNumber(modalTasso.storiciTasso[0].tassoNuovo)
                  : formatNumber(modalTasso.tassoIniziale)}%
              </strong>
            </div>

            <div className="bop-finanz__form-grid-2">
              <div className="bop-form-group">
                <label className="bop-label">Nuovo Tasso % *</label>
                <input className="bop-input" type="number" step="0.01" placeholder="3.50"
                  value={formTasso.nuovoTasso}
                  onChange={e => setFormTasso(p => ({ ...p, nuovoTasso: e.target.value }))} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Data Decorrenza *</label>
                <input className="bop-input" type="date" value={formTasso.dataDecorrenza}
                  onChange={e => setFormTasso(p => ({ ...p, dataDecorrenza: e.target.value }))} />
              </div>
            </div>

            <div className="bop-form-group">
              <label className="bop-label">Motivazione</label>
              <textarea className="bop-input" rows={2} placeholder="es. Adeguamento Euribor gennaio 2026"
                value={formTasso.motivazione}
                onChange={e => setFormTasso(p => ({ ...p, motivazione: e.target.value }))} />
            </div>

            {nuovaRataPreview !== null && (
              <div className="bop-finanz__info-preview">
                Le rate future verranno ricalcolate. La nuova rata sarà di circa <strong>{formatCurrency(nuovaRataPreview)}</strong>.
              </div>
            )}

            {tassoMsg && (
              <div className={tassoMsg.startsWith('Errore') ? 'bop-finanz__tasso-msg--error' : 'bop-finanz__tasso-msg--success'}>
                {tassoMsg}
              </div>
            )}

            {/* Storico modifiche tasso */}
            {modalTasso.storiciTasso && modalTasso.storiciTasso.length > 0 && (
              <div className="bop-finanz__storico">
                <div className="bop-finanz__storico-title">
                  Storico modifiche tasso
                </div>
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Da</th>
                        <th>A</th>
                        <th>Motivazione</th>
                        <th>Utente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalTasso.storiciTasso.map(s => (
                        <tr key={s.id}>
                          <td className="bop-finanz__cell-sm">{fmtData(s.dataDecorrenza)}</td>
                          <td className="bop-finanz__cell-sm">{formatNumber(s.tassoVecchio)}%</td>
                          <td className="bop-finanz__cell-sm bop-finanz__cell-primary">{formatNumber(s.tassoNuovo)}%</td>
                          <td className="bop-finanz__cell-secondary">{s.motivazione || '—'}</td>
                          <td className="bop-finanz__cell-xs">{s.utenteNome || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Layout>
  );
}
